import React, { type RefObject } from 'react';
import { StanButton } from '../../StanButton/StanButton';
import { BareSvg } from '@/lib';
import IconDownload from '@/assets/icons/ic-download.svg';
import IconDelete from '@/assets/icons/ic-trash.svg';
import IconCheckmarkPositive from '@/assets/icons/ic-check-circle-filled-positive.svg';
import IconCheckmarkNegative from '@/assets/icons/ic-warning-circle-filled-negative.svg';
import IconClock from '@/assets/icons/ic-clock.svg';
import { StanLoader } from '../../StanLoader/StanLoader';
import { useComponentWidth } from '@/hooks/useComponentWidth';
import { cn } from '@/lib/utils/cn';
import { downloadFile } from '@/lib/helpers';
import { FileState } from '@/hooks/useFileUpload';

export type StanFileDropZonePreviewRowProps = {
    fileState: FileState;
    idx: number;
    onDelete: (idx: number) => void;
};

function getStatusIcon(status: string) {
    switch (status) {
        case 'uploading':
            return <StanLoader />;
        case 'success':
            return <BareSvg SvgComponent={IconCheckmarkPositive} />;
        case 'error':
            return <BareSvg SvgComponent={IconCheckmarkNegative} />;
        case 'pending':
        default:
            return <BareSvg SvgComponent={IconClock} />;
    }
}

export const StanFileDropZonePreviewRow: React.FC<StanFileDropZonePreviewRowProps> = ({
    fileState: f,
    idx,
    onDelete,
}) => {
    const [ref, width] = useComponentWidth();
    const isDownloadButtonVisible = width && width > 400;

    const handleDownload = () => {
        downloadFile(f.url || '', f.file.name);
    };
    return (
        <li
            ref={ref as RefObject<HTMLLIElement>}
            className='bg-neutral-soft flex h-16 w-full items-center justify-between gap-2 overflow-hidden rounded-md px-4 py-2'
        >
            <div
                className={cn('flex items-center gap-2', {
                    'max-w-1/2': isDownloadButtonVisible,
                    'max-w-3/4': !isDownloadButtonVisible,
                })}
            >
                <span className='w-6'>{getStatusIcon(f.status)}</span>
                <span
                    className={`overflow-hidden text-sm font-semibold text-ellipsis whitespace-nowrap ${f.status === 'uploading' ? 'text-light' : 'text-dark'}`}
                >
                    {f.file.name}
                </span>
                {f.status === 'uploading' && (
                    <BareSvg
                        SvgComponent={IconDelete}
                        className='font-inter text-dark hover:text-negative cursor-pointer transition-colors duration-150'
                        removeFill
                        width={20}
                        height={20}
                        tabIndex={0}
                        onClick={() => onDelete(idx)}
                    />
                )}
            </div>

            <div className='flex max-w-1/2 items-center gap-2'>
                {f.status === 'uploading' && (
                    <span className='font-inter text-light text-xs font-semibold'>Uploading... ({f.progress}%)</span>
                )}
                {f.status === 'success' && (
                    <div className='flex items-center gap-2'>
                        {isDownloadButtonVisible && (
                            <StanButton
                                variant='outline'
                                size='xs'
                                name={`download-file-${idx}`}
                                onClick={handleDownload}
                            >
                                <BareSvg SvgComponent={IconDownload} className='mr-1' removeFill width={16} />
                                Download
                            </StanButton>
                        )}
                        {!isDownloadButtonVisible && (
                            <BareSvg
                                className='text-brand-500-main hover:text-dark cursor-pointer transition-colors duration-150'
                                SvgComponent={IconDownload}
                                width={20}
                                height={20}
                                tabIndex={0}
                                removeFill
                                onClick={handleDownload}
                            />
                        )}
                        <BareSvg
                            SvgComponent={IconDelete}
                            className='text-dark hover:text-negative cursor-pointer transition-colors duration-150'
                            removeFill
                            width={20}
                            height={20}
                            tabIndex={0}
                            onClick={() => onDelete(idx)}
                        />
                    </div>
                )}
                {f.status === 'error' && <span className='text-sm font-semibold text-red-500'>{f.error}</span>}
            </div>
        </li>
    );
};
